import { useEffect } from 'react';

export function useImageFlick(open: boolean, close: () => void) {
    useEffect(() => {
        const panel = document.querySelector<HTMLElement>('.lab-image-preview');
        if (!open || !panel) return;
        let gesture: { x: number; y: number; time: number; dx: number; dy: number; top: boolean; image: boolean; frame: HTMLElement | null; axis: 'x' | 'y' | null } | null = null;
        let suppressClickUntil = 0;
        let busy = false;
        let committing = false;
        let incomingFrame = 0;
        const timers = new Set<number>();
        const animations = new Set<Animation>();
        const targets = new Set<HTMLElement>();
        const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const clearVisuals = () => {
            animations.forEach(animation => animation.cancel()); animations.clear();
            targets.forEach(target => { target.style.transform = ''; target.style.opacity = ''; target.style.transition = ''; }); targets.clear();
        };
        const cancel = () => {
            gesture = null; busy = false; committing = false;
            timers.forEach(clearTimeout); timers.clear();
            cancelAnimationFrame(incomingFrame);
            clearVisuals();
        };
        const later = (action: () => void, duration: number) => {
            const timer = window.setTimeout(() => { timers.delete(timer); action(); }, duration);
            timers.add(timer);
        };
        const animate = (target: HTMLElement, frames: Keyframe[], duration: number) => {
            targets.add(target);
            if (typeof target.animate !== 'function') return;
            try {
                const animation = target.animate(frames, { duration, easing: 'cubic-bezier(.2,.75,.25,1)', fill: 'forwards' });
                animations.add(animation);
            } catch {}
        };
        const position = (axis: 'x' | 'y', distance: number) => axis === 'x' ? `translate3d(${distance}px,0,0)` : `translate3d(0,${distance}px,0)`;
        const start = (event: TouchEvent) => {
            if (busy && committing) return;
            cancel();
            if (!window.matchMedia('(max-width: 600px)').matches || event.touches.length !== 1 || panel.closest('[inert]') || !window.getSelection()?.isCollapsed) return;
            const target = event.target as HTMLElement;
            const frame = target.closest<HTMLElement>('.lab-progressive-image');
            if (!frame && target.closest('button, a, input, textarea, select, summary, [contenteditable="true"]')) return;
            let nested = false;
            for (let element = target; element && element !== panel; element = element.parentElement!) {
                if (element.scrollHeight > element.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(element).overflowY)) nested = true;
            }
            const touch = event.touches[0];
            const bounds = frame?.getBoundingClientRect();
            gesture = { x: touch.clientX, y: touch.clientY, time: event.timeStamp, dx: 0, dy: 0,
                top: panel.scrollTop <= 1 && !nested, image: !!bounds && touch.clientY <= bounds.top + bounds.height * .6, frame, axis: null };
        };
        const move = (event: TouchEvent) => {
            if (!gesture) return;
            if (event.touches.length !== 1 || panel.closest('[inert]')) { cancel(); return; }
            gesture.dx = event.touches[0].clientX - gesture.x;
            gesture.dy = event.touches[0].clientY - gesture.y;
            const x = Math.abs(gesture.dx), y = Math.abs(gesture.dy);
            if (!gesture.axis) {
                if (x >= 12 && x > y * 1.4) { if (gesture.image) gesture.axis = 'x'; else cancel(); }
                else if (y >= 12 && y > x * 1.4) { if (gesture.top && gesture.dy > 0) gesture.axis = 'y'; else cancel(); }
                else if (Math.max(x, y) > 24) cancel();
            }
            if (gesture?.axis) {
                if (event.cancelable) event.preventDefault();
                if (!reduced()) {
                    const target = gesture.axis === 'y' ? panel : gesture.frame!;
                    if (gesture.axis === 'y') target.style.transition = 'none';
                    const button = panel.querySelectorAll<HTMLButtonElement>('.lab-image-header-navigation button')[gesture.dx < 0 ? 1 : 0];
                    const resistance = gesture.axis === 'x' && button?.dataset.navigationState !== 'ready' ? .24 : .65;
                    const distance = (gesture.axis === 'x' ? gesture.dx : gesture.dy) * resistance;
                    targets.add(target);
                    target.style.transform = position(gesture.axis, Math.max(-160, Math.min(160, distance)));
                }
            }
        };
        const end = (event: TouchEvent) => {
            const value = gesture;
            gesture = null;
            if (!value?.axis || event.touches.length) return;
            const target = value.axis === 'y' ? panel : value.frame!;
            const distance = value.axis === 'x' ? Math.abs(value.dx) : value.dy;
            const speed = distance / Math.max(1, event.timeStamp - value.time);
            if (distance < 64 && !(distance >= 28 && speed >= .35)) {
                if (!reduced()) { busy = true; animate(target, [{ transform: target.style.transform }, { transform: position(value.axis, 0) }], 140); later(cancel, 140); }
                else cancel();
                return;
            }
            suppressClickUntil = performance.now() + 450;
            const button = panel.querySelectorAll<HTMLButtonElement>('.lab-image-header-navigation button')[value.dx < 0 ? 1 : 0];
            const allowed = value.axis === 'y' || (button?.dataset.navigationState === 'ready' && !button.disabled);
            const action = () => { if (value.axis === 'y') close(); else button?.click(); };
            if (reduced()) { cancel(); if (allowed) action(); return; }
            busy = true;
            const direction = value.axis === 'x' && value.dx < 0 ? -1 : 1;
            const from = target.style.transform || position(value.axis, 0);
            if (!allowed) {
                const missing = button?.dataset.navigationState === 'missing';
                const overshoot = direction * Math.min(80, distance * .24 + speed * 12);
                animate(target, missing ? [{ transform: from }, { transform: position(value.axis, overshoot), offset: .4 }, { transform: position(value.axis, 0) }]
                    : [{ transform: from }, { transform: position(value.axis, 0) }], missing ? 340 : 140);
                later(cancel, missing ? 340 : 140);
                return;
            }
            const selected = panel.querySelector<HTMLElement>('[data-detail-id]')?.dataset.detailId;
            committing = true;
            const duration = value.axis === 'y' ? 180 : 140;
            animate(target, [{ transform: from, opacity: 1 }, { transform: position(value.axis, direction * Math.min(window.innerHeight, distance + speed * 80)), opacity: 0 }], duration);
            later(() => {
                clearVisuals();
                if (!panel.isConnected || panel.closest('[inert]') || panel.querySelector<HTMLElement>('[data-detail-id]')?.dataset.detailId !== selected) { cancel(); return; }
                action();
                if (value.axis === 'y') { cancel(); return; }
                busy = false;
                committing = false;
                incomingFrame = requestAnimationFrame(() => {
                    if (!target.isConnected) { cancel(); return; }
                    animate(target, [{ transform: position('x', -direction * 18), opacity: .6 }, { transform: position('x', 0), opacity: 1 }], 180);
                    later(cancel, 180);
                });
            }, duration);
        };
        const click = (event: MouseEvent) => {
            if (performance.now() < suppressClickUntil && (event.target as HTMLElement).closest('.lab-progressive-image')) {
                event.preventDefault(); event.stopImmediatePropagation();
            }
        };
        panel.addEventListener('touchstart', start, { passive: true });
        panel.addEventListener('touchmove', move, { passive: false });
        panel.addEventListener('touchend', end);
        panel.addEventListener('touchcancel', cancel);
        panel.addEventListener('click', click, true);
        window.addEventListener('resize', cancel);
        return () => {
            cancel();
            panel.removeEventListener('touchstart', start); panel.removeEventListener('touchmove', move);
            panel.removeEventListener('touchend', end); panel.removeEventListener('touchcancel', cancel);
            panel.removeEventListener('click', click, true); window.removeEventListener('resize', cancel);
        };
    }, [open, close]);
}
